import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Clock, Filter, CheckCircle2, XCircle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useWebhookLogs } from '@/hooks'
import { useWebhookDeliveries } from '@/hooks/use-outbound-webhooks'
import type { WebhookLog, WebhookDelivery } from '@/lib/types'
import { EventDetailsDialog } from '@/components/webhooks/event-details-dialog'
import {
  fromWebhookLog,
  fromWebhookDelivery,
  EVENT_LABELS,
  PLATFORM_LABELS,
} from '@/components/webhooks/event-constants'
import { DirectionBadge } from '@/components/webhooks/event-ui'

type UnifiedEvent = {
  id: string
  direction: 'in' | 'out'
  source: string
  destination: string
  eventType: string
  status: 'success' | 'error' | 'pending'
  createdAt: string
  responseCode?: number
  // Dados originais para o dialog
  originalLog?: WebhookLog
  originalDelivery?: WebhookDelivery
}

interface EventsTimelineProps {
  filter?: {
    platform?: string
    direction?: 'in' | 'out'
  }
  limit?: number
  showFilters?: boolean
}

export function EventsTimeline({ filter, limit, showFilters = true }: EventsTimelineProps) {
  const [directionFilter, setDirectionFilter] = useState<'all' | 'in' | 'out'>(
    filter?.direction || 'all'
  )
  const [platformFilter, setPlatformFilter] = useState<string>(filter?.platform || 'all')

  // Fetch incoming logs
  const { data: incomingLogs, isLoading: loadingIncoming } = useWebhookLogs()

  // Fetch outgoing deliveries
  const { data: outgoingDeliveries, isLoading: loadingOutgoing } = useWebhookDeliveries(
    limit || 50,
    0,
    platformFilter !== 'all' &&
      platformFilter !== 'guru' &&
      platformFilter !== 'yampi' &&
      platformFilter !== 'instagram'
      ? (platformFilter as 'webhook' | 'klaviyo')
      : undefined
  )

  const isLoading = loadingIncoming || loadingOutgoing

  // Unify events
  const unifiedEvents: UnifiedEvent[] = []

  // Add incoming events
  if (directionFilter === 'all' || directionFilter === 'in') {
    incomingLogs?.forEach((log: WebhookLog) => {
      if (platformFilter !== 'all' && log.platform !== platformFilter) return

      unifiedEvents.push({
        id: `in-${log.id}`,
        direction: 'in',
        source: log.platform,
        destination: 'Rafobase',
        eventType: log.eventType || 'unknown',
        status:
          log.status === 'processed' ? 'success' : log.status === 'failed' ? 'error' : 'pending',
        createdAt: log.createdAt,
        originalLog: log,
      })
    })
  }

  // Add outgoing events
  if (directionFilter === 'all' || directionFilter === 'out') {
    outgoingDeliveries?.forEach((delivery: WebhookDelivery) => {
      if (platformFilter !== 'all' && delivery.platform !== platformFilter) return

      unifiedEvents.push({
        id: `out-${delivery.id}`,
        direction: 'out',
        source: 'Rafobase',
        destination: delivery.platform === 'klaviyo' ? 'klaviyo' : delivery.url || 'webhook',
        eventType: delivery.eventType,
        status:
          delivery.status === 'success'
            ? 'success'
            : delivery.status === 'failed'
              ? 'error'
              : 'pending',
        createdAt: delivery.createdAt,
        responseCode: delivery.responseCode,
        originalDelivery: delivery,
      })
    })
  }

  // Sort by date descending
  unifiedEvents.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  // Apply limit
  const displayedEvents = limit ? unifiedEvents.slice(0, limit) : unifiedEvents

  if (isLoading) {
    return <TimelineSkeleton />
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Filter className="h-4 w-4 text-muted-foreground hidden sm:block" />
          <Select
            value={directionFilter}
            onValueChange={(v) => setDirectionFilter(v as 'all' | 'in' | 'out')}
          >
            <SelectTrigger className="w-full sm:w-[150px] h-8">
              <SelectValue placeholder="Direção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="in">Recebidos</SelectItem>
              <SelectItem value="out">Enviados</SelectItem>
            </SelectContent>
          </Select>

          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-full sm:w-[150px] h-8">
              <SelectValue placeholder="Origem" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="guru">Guru</SelectItem>
              <SelectItem value="yampi">Yampi</SelectItem>
              <SelectItem value="klaviyo">Klaviyo</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="webhook">Webhooks</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Data</TableHead>
            <TableHead className="w-[80px] hidden sm:table-cell">Direção</TableHead>
            <TableHead className="hidden sm:table-cell">Origem / Destino</TableHead>
            <TableHead>Evento</TableHead>
            <TableHead className="w-[80px]">Status</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedEvents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Clock className="h-8 w-8 text-muted-foreground/50" />
                  <p>Nenhum evento encontrado</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            displayedEvents.map((event) => <EventRow key={event.id} event={event} />)
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function EventRow({ event }: { event: UnifiedEvent }) {
  const humanEventType = EVENT_LABELS[event.eventType] || event.eventType

  // Converte para EventData usando os conversores unificados
  const eventData = event.originalLog
    ? fromWebhookLog(event.originalLog)
    : event.originalDelivery
      ? fromWebhookDelivery(event.originalDelivery)
      : null

  return (
    <TableRow>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
        {formatDistanceToNow(new Date(event.createdAt), {
          addSuffix: true,
          locale: ptBR,
        })}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <DirectionBadge direction={event.direction === 'in' ? 'inbound' : 'outbound'} />
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="flex items-center gap-2">
          <PlatformBadge platform={event.direction === 'in' ? event.source : event.destination} />
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm">{humanEventType}</span>
      </TableCell>
      <TableCell>
        <InlineStatusBadge status={event.status} responseCode={event.responseCode} />
      </TableCell>
      <TableCell>{eventData && <EventDetailsDialog event={eventData} />}</TableCell>
    </TableRow>
  )
}

function PlatformBadge({ platform }: { platform: string }) {
  const label = PLATFORM_LABELS[platform] || platform

  const colors: Record<string, string> = {
    guru: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    yampi: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    klaviyo: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    instagram: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
    webhook: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    Rafobase: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  }

  return (
    <Badge variant="secondary" className={`${colors[platform] || colors.webhook} text-xs`}>
      {label}
    </Badge>
  )
}

// Status inline para tabela (diferente do StatusBadge do dialog)
function InlineStatusBadge({ status, responseCode }: { status: string; responseCode?: number }) {
  const Icon = status === 'success' ? CheckCircle2 : status === 'error' ? XCircle : Clock
  const color =
    status === 'success'
      ? 'text-green-600'
      : status === 'error'
        ? 'text-red-600'
        : 'text-yellow-600'

  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="h-4 w-4" />
      {responseCode && <span className="text-xs">{responseCode}</span>}
    </div>
  )
}

function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <Skeleton className="h-8 w-full sm:w-[150px]" />
        <Skeleton className="h-8 w-full sm:w-[150px]" />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Data</TableHead>
            <TableHead className="w-[80px] hidden sm:table-cell">Direção</TableHead>
            <TableHead className="hidden sm:table-cell">Origem / Destino</TableHead>
            <TableHead>Evento</TableHead>
            <TableHead className="w-[80px]">Status</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Skeleton className="h-5 w-16" />
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-8" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-7 w-7 rounded" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
